/**
 * salaryPdfHelpers.js — Exports PDF (fenêtre imprimable → "Enregistrer en PDF")
 * pour le menu SALAIRES : paie des stagiaires et paiement des échéances.
 */
import { toast } from "sonner";
import axios from "axios";
import { buildPayslipBody, extraFromPayslip, PAYSLIP_PRINT_CSS } from "../components/PayslipModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const KIND_LABEL = { stagiaire: "Stagiaire", journalier: "Journalier", occasionnel: "Occasionnel" };
const ALLOWANCE_LABEL = { monthly: "/ mois", daily: "/ jour", lumpsum: "forfait" };

const frDate = (iso, opts = { day: "numeric", month: "short", year: "numeric" }) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", opts); } catch { return iso; }
};

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const FOOTER = `<p style="margin-top:16px;font-size:9px;color:#94a3b8;text-align:center">
  Espace Maxo SARL · Fijrosse, Cotonou · IFU 3202214184317 · RCCM RB/COT/22 B 32037<br/>
  Document confidentiel généré depuis Caisse Pro
</p>`;

const BASE_CSS = `
  @page { size: A4; margin: 12mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color:#0f172a; margin:0; }
  header.main { padding:6px 0 12px; border-bottom:2px solid #0f172a; margin-bottom:12px; display:flex;justify-content:space-between;align-items:center }
  header.main h1 { margin:0;font-size:19px;letter-spacing:-0.02em }
  header.main .sub { color:#64748b;font-size:11px;margin-top:2px }
  table { width:100%; border-collapse:collapse; }
  thead td { padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;background:#f1f5f9 }
  tbody td { padding:5px 6px;font-size:10.5px;border-bottom:1px solid #e2e8f0 }
  tfoot td { padding:7px 6px;font-size:11px;font-weight:800;background:#ecfdf5;border-top:2px solid #065f46 }
  .mono { font-family: monospace; }
  .r { text-align:right } .c { text-align:center }
  .ok { color:#059669;font-weight:700 } .ko { color:#b45309;font-weight:700 } .muted { color:#94a3b8;font-style:italic }
  .kpis { display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:12px }
  .kpi { border:1px solid #cbd5e1;border-radius:6px;padding:8px 10px }
  .kpi .l { font-size:9px;text-transform:uppercase;letter-spacing:0.1em;color:#64748b;font-weight:700 }
  .kpi .v { font-size:15px;font-weight:800;font-family:monospace;margin-top:2px }
`;

function openPrintWindow(html, title) {
  const w = window.open("", "_blank");
  if (!w) { toast.error("Le navigateur a bloqué l'ouverture — autorisez les pop-ups pour ce site"); return; }
  w.document.open();
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${BASE_CSS}</style></head><body>${html}</body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 500);
}

/** Récapitulatif mensuel de la paie des stagiaires / non permanents. */
export function printInternsMonthPDF(summary, year, month) {
  const interns = summary?.interns || [];
  if (interns.length === 0) { toast.error("Aucun stagiaire à exporter pour ce mois"); return; }
  const period = `${MONTHS[month - 1]} ${year}`;
  const paidCount = interns.filter((i) => i.paid_this_month).length;

  const rows = interns.map((i) => {
    const inactive = !i.active_this_month;
    const status = inactive
      ? `<span class="muted">Hors période</span>`
      : i.paid_this_month
        ? `<span class="ok">✓ Payé</span>`
        : `<span class="ko">Non payé</span>`;
    return `<tr>
      <td><b>${esc(i.name)}</b><br/><span style="color:#64748b;font-size:9.5px">${esc(i.position || "—")}${i.phone ? " · " + esc(i.phone) : ""}</span></td>
      <td>${esc(KIND_LABEL[i.kind] || i.kind || "—")}</td>
      <td class="mono">${esc((i.start_date || "").slice(0, 10) || "—")}${i.end_date ? `<br/><span style="color:#94a3b8">→ ${esc(i.end_date.slice(0, 10))}</span>` : ""}</td>
      <td class="r mono">${fmt(i.allowance_amount)} <span style="color:#94a3b8;font-size:9px">${ALLOWANCE_LABEL[i.allowance_type] || ""}</span></td>
      <td class="c">${status}</td>
      <td class="mono">${i.paid_this_month ? frDate(i.payment_date) : "—"}</td>
      <td class="r mono" style="color:#065f46;font-weight:700">${i.paid_this_month ? fmt(i.paid_amount) : "—"}</td>
      <td style="font-size:9.5px;color:#475569">${i.paid_this_month ? esc(i.paid_by || "") : ""}</td>
    </tr>`;
  }).join("");

  const html = `
    <header class="main">
      <div>
        <h1>ESPACE MAXO — Paie des stagiaires & non permanents</h1>
        <div class="sub">Mois de travail : ${period} · ${summary.count || interns.length} personne(s) · ${paidCount} payée(s) · Édité le ${new Date().toLocaleString("fr-FR")}</div>
      </div>
      <div style="text-align:right;font-size:11px;color:#64748b">Total payé<br/><b style="color:#065f46;font-size:16px" class="mono">${fmt(summary.total_paid)} F CFA</b></div>
    </header>
    <div class="kpis">
      <div class="kpi"><div class="l">Actifs ce mois</div><div class="v">${summary.count || 0}</div></div>
      <div class="kpi"><div class="l">Total dû</div><div class="v">${fmt(summary.total_due)} F</div></div>
      <div class="kpi"><div class="l">Total payé</div><div class="v" style="color:#065f46">${fmt(summary.total_paid)} F</div></div>
      <div class="kpi"><div class="l">Restant à payer</div><div class="v" style="color:#b45309">${fmt(summary.total_unpaid)} F</div></div>
    </div>
    ${summary.payment_planned_date ? `<p style="font-size:10.5px;color:#475569;margin:0 0 8px">Échéance planifiée : <b>${frDate(summary.payment_planned_date, { day: "numeric", month: "long", year: "numeric" })}</b> (le 10 du mois suivant)</p>` : ""}
    <table>
      <thead><tr>
        <td>Nom · Poste</td><td>Type</td><td>Arrivée</td><td class="r">Forfait (F)</td>
        <td class="c">Statut</td><td>Payé le</td><td class="r">Montant payé (F)</td><td>Payé par</td>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr>
        <td colspan="3">TOTAL ${period.toUpperCase()}</td>
        <td class="r mono">${fmt(summary.total_due)}</td>
        <td class="c" style="font-size:9.5px">${paidCount} / ${summary.count || interns.length} payés</td>
        <td></td>
        <td class="r mono" style="color:#065f46">${fmt(summary.total_paid)}</td>
        <td style="font-size:9.5px;color:#b45309">Reste ${fmt(summary.total_unpaid)}</td>
      </tr></tfoot>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:28px;font-size:10.5px">
      <div>Visa Direction<br/><br/><br/>______________________</div>
      <div>Visa Comptabilité<br/><br/><br/>______________________</div>
    </div>
    ${FOOTER}`;
  openPrintWindow(html, `Paie stagiaires ${period}`);
}

/** Reçu de paiement individuel pour un stagiaire (mois donné). */
export function printInternReceiptPDF(intern, year, month) {
  if (!intern?.paid_this_month) { toast.error("Aucun paiement enregistré pour ce mois"); return; }
  const period = `${MONTHS[month - 1]} ${year}`;
  const ref = `REC-STG-${year}${String(month).padStart(2, "0")}-${String(intern.id || "").slice(-6).toUpperCase()}`;
  const html = `
    <header class="main">
      <div>
        <h1>ESPACE MAXO — Reçu de paiement</h1>
        <div class="sub">Stagiaire / personnel non permanent · Réf. ${ref} · Édité le ${new Date().toLocaleString("fr-FR")}</div>
      </div>
      <div style="text-align:right;font-size:11px;color:#64748b">Montant<br/><b style="color:#065f46;font-size:20px" class="mono">${fmt(intern.paid_amount)} F CFA</b></div>
    </header>
    <table style="margin-bottom:14px">
      <tbody>
        <tr><td style="width:38%;color:#64748b;font-weight:700">Bénéficiaire</td><td><b style="font-size:12px">${esc(intern.name)}</b></td></tr>
        <tr><td style="color:#64748b;font-weight:700">Poste</td><td>${esc(intern.position || "—")}</td></tr>
        <tr><td style="color:#64748b;font-weight:700">Type</td><td>${esc(KIND_LABEL[intern.kind] || intern.kind || "—")}</td></tr>
        ${intern.phone ? `<tr><td style="color:#64748b;font-weight:700">Téléphone</td><td class="mono">${esc(intern.phone)}</td></tr>` : ""}
        <tr><td style="color:#64748b;font-weight:700">Période de travail</td><td><b>${period}</b></td></tr>
        <tr><td style="color:#64748b;font-weight:700">Forfait convenu</td><td class="mono">${fmt(intern.allowance_amount)} F ${ALLOWANCE_LABEL[intern.allowance_type] || ""}</td></tr>
        <tr><td style="color:#64748b;font-weight:700">Montant versé</td><td class="mono" style="color:#065f46;font-weight:800;font-size:13px">${fmt(intern.paid_amount)} F CFA</td></tr>
        <tr><td style="color:#64748b;font-weight:700">Date de paiement</td><td>${frDate(intern.payment_date, { day: "numeric", month: "long", year: "numeric" })}</td></tr>
        <tr><td style="color:#64748b;font-weight:700">Payé par</td><td>${esc(intern.paid_by || "Administrateur")}</td></tr>
        ${intern.payment_note ? `<tr><td style="color:#64748b;font-weight:700">Note</td><td>${esc(intern.payment_note)}</td></tr>` : ""}
      </tbody>
    </table>
    <p style="font-size:10.5px;color:#475569">Je soussigné(e) <b>${esc(intern.name)}</b> reconnais avoir reçu la somme de <b>${fmt(intern.paid_amount)} F CFA</b> au titre de mes frais forfaitaires pour la période de <b>${period}</b>.</p>
    <div style="display:flex;justify-content:space-between;margin-top:36px;font-size:10.5px">
      <div>Signature du bénéficiaire<br/><br/><br/>______________________</div>
      <div>Pour Espace Maxo<br/><br/><br/>______________________</div>
    </div>
    ${FOOTER}`;
  openPrintWindow(html, `Reçu ${intern.name} ${period}`);
}

/** Tableau des échéances de paie (nets du mois M payés en M+1). */
export function printPaymentSchedulePDF(data, payYear, payMonth) {
  const entries = data?.entries || [];
  if (entries.length === 0) { toast.error("Aucun paiement à exporter pour ce mois"); return; }
  const [wy, wm] = (data.work_period || "").split("-").map(Number);
  const workLabel = wm ? `${MONTHS[wm - 1]} ${wy}` : "—";
  const payLabel = `${MONTHS[payMonth - 1]} ${payYear}`;

  const rows = entries.map((e) => `<tr>
    <td><b>${esc(e.name)}</b><br/><span style="color:#64748b;font-size:9.5px">${esc(e.role_title || "—")}${e.matricule ? " · " + esc(e.matricule) : ""}</span></td>
    <td>${workLabel}</td>
    <td class="r mono" style="color:#065f46;font-weight:700">${fmt(e.net)}</td>
    <td class="mono">${frDate(e.payment_planned_date)}</td>
    <td class="mono">${e.paid_at ? frDate(e.paid_at) : "—"}</td>
    <td class="c">${e.is_paid ? `<span class="ok">✓ Payé</span>` : `<span class="ko">À payer</span>`}</td>
    <td class="mono">${e.mobile_money ? esc(e.mobile_money) : `<span class="muted">—</span>`}</td>
  </tr>`).join("");

  const html = `
    <header class="main">
      <div>
        <h1>ESPACE MAXO — Échéances de paie</h1>
        <div class="sub">Décaissements ${payLabel} · Travail de ${workLabel} · Échéance ${frDate(data.payment_planned_date)} · ${entries.length} employé(s) · Édité le ${new Date().toLocaleString("fr-FR")}</div>
      </div>
      <div style="text-align:right;font-size:11px;color:#64748b">Total planifié<br/><b style="color:#065f46;font-size:16px" class="mono">${fmt(data.totals?.planned)} F CFA</b></div>
    </header>
    <div class="kpis" style="grid-template-columns:repeat(3,1fr)">
      <div class="kpi"><div class="l">À payer</div><div class="v" style="color:#b45309">${fmt(data.totals?.unpaid)} F</div><div style="font-size:9px;color:#94a3b8">${data.counts?.unpaid || 0} employé(s)</div></div>
      <div class="kpi"><div class="l">Déjà payés</div><div class="v" style="color:#065f46">${fmt(data.totals?.paid)} F</div><div style="font-size:9px;color:#94a3b8">${data.counts?.paid || 0} employé(s)</div></div>
      <div class="kpi"><div class="l">Total planifié</div><div class="v">${fmt(data.totals?.planned)} F</div><div style="font-size:9px;color:#94a3b8">${data.counts?.total || 0} employé(s)</div></div>
    </div>
    <table>
      <thead><tr>
        <td>Employé</td><td>Mois travaillé</td><td class="r">Net (F)</td><td>Payable le</td><td>Payé le</td><td class="c">Statut</td><td>Mobile Money</td>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr>
        <td colspan="2">TOTAL ÉCHÉANCE ${frDate(data.payment_planned_date).toUpperCase()}</td>
        <td class="r mono" style="color:#065f46">${fmt(data.totals?.planned)}</td>
        <td colspan="4" style="font-size:10px;color:#475569">Payé ${fmt(data.totals?.paid)} F · Reste <span style="color:#b45309">${fmt(data.totals?.unpaid)} F</span></td>
      </tr></tfoot>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:28px;font-size:10.5px">
      <div>Visa Direction<br/><br/><br/>______________________</div>
      <div>Visa Comptabilité<br/><br/><br/>______________________</div>
    </div>
    ${FOOTER}`;
  openPrintWindow(html, `Échéances paie ${payLabel}`);
}

/** Tous les bulletins de paie sauvegardés d'un mois, en un seul document (1 bulletin / page). */
export async function printMonthlyPayslipsPDF({ employees = [], year, month }) {
  if (employees.length === 0) { toast.error("Aucun employé à exporter"); return; }
  const period = `${MONTHS[month - 1]} ${year}`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Le navigateur a bloqué l'ouverture — autorisez les pop-ups pour ce site"); return; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Bulletins ${period}</title>
<style>body{font-family:system-ui,sans-serif;background:#0f172a;color:white;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;text-align:center}
.sp{width:44px;height:44px;border:4px solid #334155;border-top-color:#10b981;border-radius:50%;animation:s 1s linear infinite;margin:0 auto 16px}
@keyframes s{to{transform:rotate(360deg)}}</style></head>
<body><div><div class="sp"></div><p>Génération des bulletins…</p><p style="opacity:0.5;font-size:12px">${employees.length} employé(s) · ${esc(period)}</p></div></body></html>`);
  w.document.close();

  const slips = await Promise.all(employees.map((e) =>
    axios.get(`${API}/salaries/${e.id}/payslip/${year}/${month}`).then((r) => r.data).catch(() => null)
  ));
  const valid = slips.filter(Boolean);
  if (valid.length === 0) { w.close(); toast.error(`Aucune fiche de paie trouvée pour ${period}`); return; }

  const pages = valid.map((d, i) =>
    `<section style="${i < valid.length - 1 ? "page-break-after:always;break-after:page;" : ""}">${buildPayslipBody(d, extraFromPayslip(d))}</section>`
  ).join("");
  const totalNet = valid.reduce((s, d) => s + (d.net_to_pay || 0), 0);
  const unsaved = valid.filter((d) => !d.updated_at).length;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Bulletins de paie ${esc(period)}</title>
<style>${PAYSLIP_PRINT_CSS}
  .batch-cover { page-break-after:always; break-after:page; padding:20px 0; }
  .batch-cover h1 { font-size:20px; margin:0 0 4px; color:#0F1F3D; }
  .batch-cover table td { padding:5px 8px; font-size:11px; border-bottom:1px solid #e2e8f0; }
  .batch-cover thead td { background:#f1f5f9; font-size:9.5px; text-transform:uppercase; letter-spacing:0.08em; font-weight:700; color:#475569; }
  .batch-cover tfoot td { background:#ecfdf5; font-weight:800; border-top:2px solid #065f46; }
</style></head><body>
  <div class="batch-cover">
    <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #a92e2e;padding-bottom:8px;margin-bottom:12px">
      <div>
        <h1>ESPACE MAXO — Bulletins de paie ${esc(period)}</h1>
        <div style="font-size:11px;color:#64748b">${valid.length} bulletin(s) · Édité le ${new Date().toLocaleString("fr-FR")}${unsaved ? ` · ${unsaved} fiche(s) non sauvegardée(s) (valeurs par défaut)` : ""}</div>
      </div>
      <div style="text-align:right;font-size:11px;color:#64748b">Total net à payer<br/><b style="color:#065f46;font-size:16px;font-family:monospace">${fmt(totalNet)} F CFA</b></div>
    </div>
    <table style="width:100%;border-collapse:collapse">
      <thead><tr><td>#</td><td>Employé</td><td>Téléphone</td><td>Poste</td><td style="text-align:center">Jours</td><td style="text-align:right">Brut</td><td style="text-align:right">Retenues</td><td style="text-align:right">Net à payer</td><td style="text-align:center">Statut</td></tr></thead>
      <tbody>${valid.map((d, i) => `<tr>
        <td style="color:#94a3b8">${i + 1}</td>
        <td><b>${esc(d.employee?.name)}</b>${d.employee?.matricule ? `<br/><span style="font-size:9.5px;color:#64748b">${esc(d.employee.matricule)}</span>` : ""}</td>
        <td style="font-family:monospace;white-space:nowrap">${d.employee?.phone ? esc(d.employee.phone) : '<span style="color:#94a3b8">—</span>'}</td>
        <td>${esc(d.employee?.role_title || "—")}</td>
        <td style="text-align:center">${d.days_paid ?? 0}</td>
        <td style="text-align:right;font-family:monospace">${fmt(d.gains_total || d.gross)}</td>
        <td style="text-align:right;font-family:monospace;color:#991b1b">${fmt(d.total_retenues)}</td>
        <td style="text-align:right;font-family:monospace;font-weight:800;color:#065f46">${fmt(d.net_to_pay)}</td>
        <td style="text-align:center;font-size:10px">${d.paid_at ? '<span style="color:#059669;font-weight:700">Payé</span>' : d.signed ? '<span style="color:#0369a1;font-weight:700">Signé</span>' : d.updated_at ? "Sauvegardé" : '<span style="color:#b45309">Non sauvegardé</span>'}</td>
      </tr>`).join("")}</tbody>
      <tfoot><tr><td colspan="5">TOTAL ${esc(period.toUpperCase())}</td>
        <td style="text-align:right;font-family:monospace">${fmt(valid.reduce((s, d) => s + (d.gains_total || d.gross || 0), 0))}</td>
        <td style="text-align:right;font-family:monospace;color:#991b1b">${fmt(valid.reduce((s, d) => s + (d.total_retenues || 0), 0))}</td>
        <td style="text-align:right;font-family:monospace;color:#065f46">${fmt(totalNet)}</td><td></td></tr></tfoot>
    </table>
    ${FOOTER}
  </div>
  ${pages}
</body></html>`;

  try {
    w.document.open();
    w.document.write(html);
    w.document.close();
    const doPrint = async () => {
      try {
        const imgs = Array.from(w.document.images || []);
        await Promise.all(imgs.map((img) => img.complete ? Promise.resolve() : new Promise((res) => { img.onload = res; img.onerror = res; setTimeout(res, 2000); })));
      } catch { /* noop */ }
      try { w.focus(); w.print(); } catch { /* fenêtre fermée */ }
    };
    setTimeout(doPrint, 200);
  } catch (e) {
    toast.error("Impossible de générer le PDF : " + e.message);
  }
}
